import React, { useEffect, useState, useCallback, useRef } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Card, Table, Col, Row, Checkbox } from 'antd'
import commonStyles from '../../../Utils/Common.less'
import type { ColumnsType, TableProps } from 'antd/es/table'
import {
    GetPerformInvoice,
    GetSetMark,
} from '../../../Services/Invoicing/Invoice'
import {
    type ArchiveInvoiceState,
    type ArcheivedInvoiceListDataTypeInterface,
    type ArchivedInvoiceProps,
} from '../../../Types/Invoicing/Invoice'
import SearchInput from '../../../Components/SearchInput'
import calculatePagination from '../../../Utils/Pagination'
import InvoicePreparationExpandView from './invoicepreparetionExpandView'
import { formatdate } from '../../../Utils/dateFormat'

const ArchivedInvoice = (props: ArchivedInvoiceProps): React.ReactElement => {
    const dispatch = useDispatch()
    const {
        ArchivedInvoice,
        selectedCategoryId,
        selectedServiceId,
        ArchivedInvoiceCount,
    } = props
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [searchString, setSearchString] = useState('=NA=')
    const hasFetchedOnce = useRef(false)
    const getTableData = useCallback(() => {
        GetPerformInvoice(
            dispatch,
            searchString,
            2,
            selectedCategoryId,
            selectedServiceId,
            currentPage,
            itemsPerPage
        )
    }, [
        searchString,
        currentPage,
        itemsPerPage,
        selectedCategoryId,
        selectedServiceId,
    ])

    useEffect(() => {
        if (!hasFetchedOnce.current) {
            hasFetchedOnce.current = true
            return
        }
        getTableData()
    }, [getTableData])

    const handleCheckboxChange = (record: number): void => {
        void GetSetMark(dispatch, record)
        getTableData()
    }

    useEffect(() => {
        getTableData()
    }, [getTableData])

    const handlePageChange = (pageNo: number, pageSize: number): void => {
        window.scrollTo(0, 0)
        setCurrentPage(pageNo)
        setItemsPerPage(pageSize)
    }
    const handlePageSizeChange = (pageSize: number): void => {
        setItemsPerPage(pageSize)
        setCurrentPage(1) // Reset to first page when page size changes
    }

    const pagination = calculatePagination(
        currentPage,
        ArchivedInvoiceCount,
        itemsPerPage,
        handlePageSizeChange
    )
    const columns: ColumnsType<ArcheivedInvoiceListDataTypeInterface> = [
        {
            width: 100,
            title: 'Number',
            dataIndex: 'clientNumber',
        },
        {
            title: 'Client',
            dataIndex: 'referenceName',
        },
        {
            title: 'Period',
            dataIndex: 'periodStartDates',
            render: (_, record) => (
                <span>
                    Period Of {formatdate(record.periodStartDate)} to{' '}
                    {formatdate(record.periodEndDate)}
                </span>
            ),
        },
        {
            width: 120,
            title: 'Services',
            dataIndex: 'serviceName',
        },
        {
            title: 'Current Status',
            dataIndex: 'currentStatus',
        },
        {
            title: '',
            key: 'checkbox',
            render: (_, record: ArcheivedInvoiceListDataTypeInterface) => {
                const str = record.currentStatus.substring(0, 7)
                return (
                    str.localeCompare('INVOICE') === 0 &&
                    (record.isSent === 0 || record.isPaid === 0) && (
                        <div>
                            <Checkbox
                                checked
                                onChange={() => {
                                    handleCheckboxChange(
                                        record.serviceProcessId
                                    )
                                }}
                            />
                        </div>
                    )
                )
            },
        },
    ]

    const onChangeTableParams: TableProps<ArcheivedInvoiceListDataTypeInterface>['onChange'] =
        (pagination, filters, sorter: any, extra) => {
            handlePageChange(pagination.current ?? 1, pagination.pageSize ?? 10)
        }

    const onChangeText = (data: string): void => {
        onChangeFilter(data)
    }

    const onChangeFilter = (data: string): void => {
        if (data?.length !== 0) {
            setSearchString(data)
        } else {
            setSearchString('=NA=')
        }
    }
    const expandableData = (
        record: ArcheivedInvoiceListDataTypeInterface
    ): React.ReactElement => {
        return <InvoicePreparationExpandView InvoiceData={record} />
    }
    const mappdata = ArchivedInvoice?.map((item) => ({
        ...item,
        key: item.serviceProcessId,
    }))

    return (
        <Card className={commonStyles.card}>
            <Row gutter={16} align="middle">
                <div className={commonStyles.corpList}>
                    <Col span={16}>
                        <SearchInput
                            placeHolder={'Search CompanyName by keywords'}
                            onSearch={onChangeFilter}
                            onChange={onChangeText}
                            width="478"
                        />
                    </Col>
                </div>
            </Row>
            <div className={commonStyles.table}>
                <Table
                    columns={columns}
                    dataSource={mappdata}
                    onChange={onChangeTableParams}
                    pagination={pagination}
                    expandable={{
                        expandedRowRender: expandableData,
                        rowExpandable: () => true,
                    }}
                />
            </div>
        </Card>
    )
}

const mapStateToProps = (state: any): ArchiveInvoiceState => {
    return {
        ArchivedInvoice: state.invoice.ArchivedInvoice,
        ArchivedInvoiceCount: state.invoice.ArchivedInvoiceCount,
    }
}

export default connect(mapStateToProps)(ArchivedInvoice)
